// Filtres de la liste des trajets (lot 056): lus dans l'adresse, envoyés au
// serveur. La liste, l'impression et l'envoi au responsable reçoivent les mêmes
// paramètres: ce que montre l'écran est ce qui s'imprime.
import { DELIVERY_PHASE_STATUSES, IMPORT_PHASE_STATUSES } from './tripPhases';

/** Groupes d'étapes, mêmes codes que `services/trip_list_query.py`. */
export const GROUPES_ETAPE = ['all', 'importation', 'livraison', 'active', 'customs', 'completed'];

/** Nom de chaque filtre dans l'adresse (`?status=customs&depot=…`). */
export const NOMS_URL = {
  etape: 'status',
  convoi: 'operation',
  transporteur: 'transporteur',
  depot: 'depot',
  parking: 'parking',
  docSortie: 'docsortie',
  chargeDu: 'charge_du',
  chargeAu: 'charge_au',
  douaneDu: 'douane_du',
  douaneAu: 'douane_au',
  q: 'q',
};

const JOUR = /^\d{4}-\d{2}-\d{2}$/;
const ouiNon = (valeur) => (valeur === 'oui' || valeur === 'non' ? valeur : '');
const jour = (valeur) => (JOUR.test(valeur || '') ? valeur : '');

/** Filtres de l'écran à partir de l'adresse; toute valeur inattendue est ignorée. */
export const lireFiltresTrajets = (searchParams) => {
  const lire = (cle) => (searchParams.get(NOMS_URL[cle]) || '').trim();
  const etape = lire('etape').toLowerCase();
  return {
    etape: GROUPES_ETAPE.includes(etape) ? etape : 'all',
    convoi: lire('convoi') || 'all',
    transporteur: lire('transporteur'),
    depot: lire('depot').toUpperCase(),
    parking: ouiNon(lire('parking')),
    docSortie: ouiNon(lire('docSortie')),
    chargeDu: jour(lire('chargeDu')),
    chargeAu: jour(lire('chargeAu')),
    douaneDu: jour(lire('douaneDu')),
    douaneAu: jour(lire('douaneAu')),
    q: lire('q'),
  };
};

/**
 * Paramètres de `GET /trips`, `GET /print/trips` et `POST /print/trips/send`.
 * Seuls les filtres actifs partent; jamais la pagination.
 */
export const parametresServeur = (filtres) => {
  const p = {};
  if (filtres.etape && filtres.etape !== 'all') p.status_group = filtres.etape;
  if (filtres.convoi && filtres.convoi !== 'all') p.operation_id = filtres.convoi;
  if (filtres.transporteur) p.transporter_id = filtres.transporteur;
  if (filtres.depot) p.loading_depot = filtres.depot;
  if (filtres.parking) p.parked = filtres.parking === 'oui';
  if (filtres.docSortie) p.has_exit_document = filtres.docSortie === 'oui';
  if (filtres.chargeDu) p.loading_date_from = filtres.chargeDu;
  if (filtres.chargeAu) p.loading_date_to = filtres.chargeAu;
  if (filtres.douaneDu) p.customs_exit_date_from = filtres.douaneDu;
  if (filtres.douaneAu) p.customs_exit_date_to = filtres.douaneAu;
  if (filtres.q) p.q = filtres.q;
  return p;
};

/** Nombre de filtres posés (la recherche comprise). */
export const nombreFiltresActifs = (filtres) => Object.keys(parametresServeur(filtres)).length;

/** Nombre de bornes de dates posées (le bouton « Dates » l'affiche). */
export const nombreDates = (filtres) =>
  ['chargeDu', 'chargeAu', 'douaneDu', 'douaneAu'].filter((cle) => filtres[cle]).length;

/**
 * Change des filtres dans l'adresse: une valeur vide (ou « tous ») retire le
 * paramètre. Rend une nouvelle `URLSearchParams`.
 */
export const avecFiltres = (precedent, changements) => {
  const suivant = new URLSearchParams(precedent);
  Object.entries(changements).forEach(([cle, valeur]) => {
    const nom = NOMS_URL[cle];
    if (!nom) return;
    if (valeur === undefined || valeur === null || valeur === '' || valeur === 'all') suivant.delete(nom);
    else suivant.set(nom, String(valeur));
  });
  return suivant;
};

/** Tous les filtres retirés de l'adresse (la taille de page est gardée). */
export const sansFiltres = (precedent) => {
  const suivant = new URLSearchParams(precedent);
  Object.values(NOMS_URL).forEach((nom) => suivant.delete(nom));
  return suivant;
};

// --- Lecture hors ligne ------------------------------------------------------
// Sans réseau, la liste vient des trajets gardés dans l'appareil: les mêmes
// filtres s'appliquent ici, avec les règles de `services/trip_list_query.py`.
// Le transporteur n'est pas sur le trajet gardé en local (il vit dans la fiche
// du camion de la société): ce filtre est ignoré hors ligne.

// « À la douane »: la douane d'entrée seulement, comme le serveur.
const STATUTS_DOUANE = ['AT_CUSTOMS_ENTRY'];
const STATUTS_FINAUX = ['COMPLETED', 'CANCELLED'];
/** Chiffres minimum pour qu'une recherche lise aussi les téléphones. */
const CHIFFRES_TELEPHONE_MIN = 4;
const RECHERCHE_TELEPHONE = /^[\d\s.+\-()/]+$/;

/** Plaque, N° de bon ou de document sans séparateurs, en majuscules. */
const compact = (valeur) => String(valeur ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const chiffres = (valeur) => String(valeur ?? '').replace(/\D/g, '');
const depotNormalise = (valeur) => String(valeur ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
const jourDe = (valeur) => String(valeur ?? '').slice(0, 10);

const lireOuiNon = (valeur) => {
  if (valeur === true || valeur === false) return valeur;
  const texte = String(valeur ?? '').trim().toLowerCase();
  if (['1', 'true', 'oui', 'yes', 'on'].includes(texte)) return true;
  if (['0', 'false', 'non', 'no', 'off'].includes(texte)) return false;
  return null;
};

const dansGroupe = (statut, groupe) => {
  const code = String(statut ?? '');
  if (groupe === 'active') return Boolean(code) && !STATUTS_FINAUX.includes(code);
  if (groupe === 'customs') return STATUTS_DOUANE.includes(code);
  if (groupe === 'completed') return code === 'COMPLETED';
  if (groupe === 'importation') return IMPORT_PHASE_STATUSES.includes(code);
  if (groupe === 'livraison') return DELIVERY_PHASE_STATUSES.includes(code);
  return true;
};

/** Le trajet répond-il à la recherche ? Plaques, bon, document, camion, chauffeur. */
const correspondALaRecherche = (trajet, texte, camion, chauffeur) => {
  const code = compact(texte);
  const brut = String(texte).toLowerCase();
  const telephone = RECHERCHE_TELEPHONE.test(texte) ? chiffres(texte) : '';
  if (code) {
    const champs = [
      trajet.tractor_plate,
      trajet.tank_plate,
      trajet.loading_voucher_number,
      trajet.exit_document_number,
      camion?.matricule,
      camion?.tank_number,
    ];
    if (champs.some((champ) => compact(champ).includes(code))) return true;
  }
  if (String(chauffeur?.full_name ?? '').toLowerCase().includes(brut)) return true;
  return telephone.length >= CHIFFRES_TELEPHONE_MIN && chiffres(chauffeur?.phone).includes(telephone);
};

/**
 * Filtre des trajets gardés en local avec les paramètres du serveur
 * (`parametresServeur`, plus `status`, `truck_id`, `driver_id`). Les camions et
 * les chauffeurs ne servent qu'à la recherche.
 */
export const filtrerTrajetsLocalement = (trajets, params = {}, { camions = [], chauffeurs = [] } = {}) => {
  const liste = Array.isArray(trajets) ? trajets : [];
  const statut = String(params.status ?? '').trim().toUpperCase();
  const groupe = String(params.status_group ?? '').trim().toLowerCase();
  const depot = depotNormalise(params.loading_depot);
  const enParking = lireOuiNon(params.parked);
  const avecDocument = lireOuiNon(params.has_exit_document);
  const recherche = String(params.q ?? '').trim().replace(/\s+/g, ' ');
  const camionsParId = new Map(camions.map((camion) => [camion?.id, camion]));
  const chauffeursParId = new Map(chauffeurs.map((chauffeur) => [chauffeur?.id, chauffeur]));
  const dansLaPeriode = (valeur, du, au) => {
    if (!du && !au) return true;
    const jour = jourDe(valeur);
    return Boolean(jour) && (!du || jour >= du) && (!au || jour <= au);
  };

  return liste.filter((trajet) => {
    if (!trajet) return false;
    if (statut && String(trajet.status ?? '').toUpperCase() !== statut) return false;
    if (groupe && !dansGroupe(trajet.status, groupe)) return false;
    for (const nom of ['truck_id', 'driver_id', 'operation_id', 'lot_id']) {
      if (params[nom] && trajet[nom] !== params[nom]) return false;
    }
    if (depot && depotNormalise(trajet.loading_depot) !== depot) return false;
    if (enParking !== null) {
      const stationne = Boolean(trajet.parked_at) && !trajet.parking_left_at;
      if (stationne !== enParking) return false;
    }
    if (avecDocument !== null && Boolean(trajet.exit_document_number) !== avecDocument) return false;
    if (!dansLaPeriode(trajet.loading_date, params.loading_date_from, params.loading_date_to)) return false;
    if (!dansLaPeriode(trajet.customs_exit_date, params.customs_exit_date_from, params.customs_exit_date_to)) {
      return false;
    }
    if (
      recherche
      && !correspondALaRecherche(trajet, recherche, camionsParId.get(trajet.truck_id), chauffeursParId.get(trajet.driver_id))
    ) {
      return false;
    }
    return true;
  });
};
